import { useDeferredValue, useState } from "react";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { Link } from "react-router-dom";
import { ArrowRight, Ban, CheckCheck, Search, ShieldAlert } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Input, PageHeader, TableSkeleton } from "../components/ui";
import { CATEGORY_LABELS, formatDate, signalLabel } from "../lib/format";

type Kind = "evasion" | "blacklisted";

const VIEWS: Record<Kind, { titre: string; vide: string; videDetail: string }> = {
  evasion: {
    titre: "Contournements de ban",
    vide: "Aucun contournement détecté",
    videDetail: "Aucun joueur sanctionné n’est revenu sous un autre compte lors des derniers contrôles.",
  },
  blacklisted: {
    titre: "Joueurs blacklistés repérés",
    vide: "Aucun joueur blacklisté repéré",
    videDetail: "Aucun joueur de la blacklist ne s’est inscrit lors des derniers contrôles.",
  },
};

type Sanction = {
  id: string;
  category: keyof typeof CATEGORY_LABELS;
  reason?: string;
  serversBan?: string;
  status: "active" | "lifted";
  addedAt: number;
  liftedAt?: number;
  liftReason?: string;
  epicId?: string;
  epicPseudo?: string;
  discordId?: string;
  discordPseudo?: string;
  lookup?: string;
  signals: Array<{ signal: string; matchedValue: string; blacklistValue: string }>;
  createdHere: boolean;
};

/**
 * Une sanction du parcours du joueur : ce qui lui a été reproché, sur quels serveurs, quand, et
 * ce qui rattache l'inscription du jour à cette fiche.
 */
function SanctionCard({ sanction, rang }: { sanction: Sanction; rang: number }) {
  return (
    <li className={`sanction${sanction.createdHere ? " sanction--new" : ""}`}>
      <div className="sanction__head">
        <span className="sanction__rank">{rang}</span>
        <div>
          <strong>{sanction.epicPseudo || sanction.discordPseudo || "Compte sans pseudo"}</strong>
          <small>Sanctionné le {formatDate(sanction.addedAt)}</small>
        </div>
        <span className="sanction__tags">
          <Badge tone="neutral">{CATEGORY_LABELS[sanction.category]}</Badge>
          {sanction.status === "lifted"
            ? <Badge tone="success">Levée</Badge>
            : <Badge tone="danger">Active</Badge>}
          {sanction.createdHere && <Badge tone="evasion">Ajoutée à l’instant</Badge>}
        </span>
      </div>

      <dl className="sanction__facts">
        {sanction.reason && <><dt>Raison</dt><dd>{sanction.reason}</dd></>}
        {sanction.serversBan && <><dt>Serveurs</dt><dd>{sanction.serversBan}</dd></>}
        {sanction.epicId && <><dt>Epic ID</dt><dd className="mono-wrap">{sanction.epicId}</dd></>}
        {sanction.discordId && <><dt>Discord ID</dt><dd className="mono-wrap">{sanction.discordId}</dd></>}
        {sanction.liftedAt && (
          <><dt>Levée</dt><dd>{formatDate(sanction.liftedAt)}{sanction.liftReason ? ` — ${sanction.liftReason}` : ""}</dd></>
        )}
      </dl>

      <div className="sanction__signals">
        {sanction.signals.map((item, index) => (
          <span key={`${item.signal}-${index}`} title={`${item.matchedValue} ↔ ${item.blacklistValue}`}>
            {signalLabel(item.signal)}
          </span>
        ))}
      </div>

      {sanction.lookup && (
        <Link className="text-link" to={`/blacklist?q=${encodeURIComponent(sanction.lookup)}`}>
          Ouvrir la fiche <ArrowRight size={14} />
        </Link>
      )}
    </li>
  );
}

export function SuspectsPage() {
  const me = useQuery(api.users.me);
  const [kind, setKind] = useState<Kind>("evasion");
  const [purging, setPurging] = useState(false);
  const [deleting, setDeleting] = useState<{ id: Id<"suspects">; nom: string } | null>(null);
  const remove = useMutation(api.suspects.remove);
  const removeAll = useMutation(api.suspects.removeAll);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<"all" | keyof typeof CATEGORY_LABELS>("all");
  // La frappe est différée : sans cela, chaque caractère relancerait une requête paginée.
  const recherche = useDeferredValue(search);
  const { results, status, loadMore } = usePaginatedQuery(
    api.suspects.list,
    { kind, search: recherche.trim() || undefined, category: category === "all" ? undefined : category },
    { initialNumItems: 25 },
  );
  const view = VIEWS[kind];
  const admin = me?.role === "admin";

  const purge = () => removeAll({ kind });

  return <>
    <PageHeader
      title="Contournements"
      description="Les joueurs repérés lors des contrôles de tournoi, avec la preuve du rapprochement. Les alertes de surveillance se traitent dans l’onglet Surveillance."
      actions={admin && results.length > 0
        ? <Button variant="secondary" onClick={() => setPurging(true)}><CheckCheck size={15} /> Tout marquer comme vu</Button>
        : undefined}
    />
    <Card className="toolbar">
      <div className="segmented" role="group" aria-label="Type de détection">
        <button type="button" aria-pressed={kind === "evasion"} className={kind === "evasion" ? "active" : ""} onClick={() => setKind("evasion")}>
          <ShieldAlert size={15} aria-hidden="true" /> Contournements de ban
        </button>
        <button type="button" aria-pressed={kind === "blacklisted"} className={kind === "blacklisted" ? "active" : ""} onClick={() => setKind("blacklisted")}>
          <Ban size={15} aria-hidden="true" /> Déjà blacklistés
        </button>
      </div>
      <div className="search-field">
        <Search size={17} aria-hidden="true" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher un pseudo ou un identifiant…"
          aria-label="Rechercher dans le journal"
        />
      </div>
      <select className="input" value={category} onChange={(event) => setCategory(event.target.value as typeof category)} aria-label="Catégorie">
        <option value="all">Toutes les catégories</option>
        {Object.entries(CATEGORY_LABELS).map(([valeur, libelle]) => <option key={valeur} value={valeur}>{libelle}</option>)}
      </select>
    </Card>
    {kind === "evasion" && results.length > 0 && (
      <p className="muted">Ces joueurs ont réutilisé un identifiant déjà sanctionné en changeant l’autre. Ils ont été ajoutés automatiquement à la blacklist.</p>
    )}
    {status === "LoadingFirstPage" ? <TableSkeleton /> : results.length === 0 ? (
      <EmptyState title={view.vide} description={view.videDetail} />
    ) : (
      <>
        <div className="suspect-grid">
          {results.map((suspect) => {
            const player = suspect.registrant as Record<string, string>;
            const sanctions = (suspect.sanctions ?? []) as Sanction[];
            const identifiants = [player.epicId, player.discordId].filter(Boolean);
            const debut = sanctions.length ? formatDate(sanctions[0].addedAt) : "";
            const fin = sanctions.length ? formatDate(sanctions[sanctions.length - 1].addedAt) : "";
            const periode = sanctions.length < 2 ? "" : debut === fin ? `le ${debut}` : `de ${debut} à ${fin}`;
            return (
              <Card key={suspect._id} className="suspect-card">
                <div className="suspect-card__top">
                  <Badge tone={suspect.evasion ? "evasion" : "danger"}>
                    {suspect.evasion ? "Contournement de ban" : "Déjà blacklisté"}
                  </Badge>
                  <span>Repéré le {formatDate(suspect.createdAt)}</span>
                </div>

                <h3>{player.epicName || player.discordName || player.wlsName || "Joueur sans pseudo"}</h3>
                <p className="mono-wrap">{identifiants.length ? identifiants.join(" · ") : "Aucun identifiant"}</p>

                <p className="sanction__count">
                  {sanctions.length} sanction{sanctions.length > 1 ? "s" : ""} à son nom
                  {/* Les entrées migrées des feuilles partagent la date de l'import : annoncer un
                      intervalle « du X au X » n'apprendrait rien. */}
                  {periode ? `, ${periode}` : ""}
                </p>
                <ol className="sanction-list">
                  {sanctions.map((sanction, index) => (
                    <SanctionCard key={sanction.id} sanction={sanction} rang={index + 1} />
                  ))}
                </ol>

                {admin && (
                  <div className="suspect-card__footer">
                    <Button variant="ghost" className="button--icon" onClick={() => setDeleting({ id: suspect._id, nom: player.epicName || player.discordName || player.wlsName || "ce signalement" })} aria-label="Marquer ce signalement comme vu">
                      <CheckCheck size={15} />
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
        {status === "CanLoadMore" && <div className="load-more"><Button variant="secondary" onClick={() => loadMore(25)}>Afficher plus</Button></div>}
      </>
    )}
    {deleting && (
      <ConfirmDialog
        title="Marquer ce signalement comme vu ?"
        description="Il quittera ce journal, mais restera visible dans son contrôle et dans la blacklist."
        target={deleting.nom}
        confirmLabel="Marquer comme vu"
        onConfirm={() => remove({ suspectId: deleting.id })}
        onClose={() => setDeleting(null)}
      />
    )}
    {purging && (
      <ConfirmDialog
        title={`Tout marquer comme vu dans « ${view.titre} » ?`}
        description="Les notifications quitteront ce journal, sans supprimer les résultats des contrôles ni les entrées de blacklist."
        confirmLabel="Tout marquer comme vu"
        onConfirm={purge}
        onClose={() => setPurging(false)}
      />
    )}
  </>;
}
